"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import tourGuideAnim from "@/assets/lottie/tour-guide-light.json";
import tourGuideAnimDark from "@/assets/lottie/tour-guide-dark.json";
import { tourGuideData } from "@/utils/constants";
import { useDeviceType } from "@/hooks/useDeviceType";
import { useTheme } from "next-themes";
import { useTranslations } from "next-intl";
import dynamic from "next/dynamic";

const Lottie = dynamic(() => import("lottie-react"), {
  ssr: false,
  loading: () => null,
});

export default function TourGuide() {
  const [step, setStep] = useState<number | null>(null);
  const [idleAnim, setIdleAnim] = useState({ y: 0, rotate: 0 });
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const deviceType = useDeviceType();
  const isMobile = deviceType === "mobile";
  const t = useTranslations("tourGuide");
  const { resolvedTheme } = useTheme();

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false
    );
  }, [mounted]);

  const shownSteps = useRef<Set<number>>(new Set());

  const showStep = (sectionId: string) => {
    const mapping: Record<string, number> = {
      Header: 1,
      About: 2,
      Experience: 3,
      Projects: 4,
      Skills: 5,
      Testimonials: 6,
      Contact: 7,
    };
    const stepNum = mapping[sectionId];
    if (stepNum && !shownSteps.current.has(stepNum)) {
      shownSteps.current.add(stepNum);
      setStep(stepNum);
    }
  };

  useEffect(() => {
    if (!mounted || isMobile || typeof window === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries)
          if (entry.isIntersecting) showStep(entry.target.id);
      },
      { threshold: 0.5, rootMargin: "0px 0px -10%" }
    );

    const header = document.getElementById("Header");
    if (header) observer.observe(header);
    document
      .querySelectorAll("section")
      .forEach((sec) => observer.observe(sec));

    return () => observer.disconnect();
  }, [mounted, isMobile]);

  useEffect(() => {
    if (step === null) return;
    const timer = setTimeout(() => setStep(null), 5000);
    return () => clearTimeout(timer);
  }, [step]);

  useEffect(() => {
    if (step === null || prefersReducedMotion) return;

    const start = () => {
      const id = setInterval(() => {
        setIdleAnim({
          y: Math.random() * 6 - 3,
          rotate: Math.random() * 6 - 3,
        });
      }, 2000 + Math.random() * 2000);
      return () => clearInterval(id);
    };

    const cleanup = start();
    return cleanup;
  }, [step, prefersReducedMotion]);

  const stepInfo = step !== null ? tourGuideData[step] : null;

  const lottieData = useMemo(
    () => (resolvedTheme === "dark" ? tourGuideAnimDark : tourGuideAnim),
    [resolvedTheme]
  );

  if (!mounted || isMobile || prefersReducedMotion) return null;

  return (
    <AnimatePresence>
      {stepInfo && (
        <motion.div
          key={`${resolvedTheme}-${step}`}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          transition={{ type: "spring", stiffness: 70, damping: 12 }}
          className={`fixed bottom-10 flex items-center z-50 ${
            stepInfo.side === "left" ? "left-5" : "right-5 flex-row-reverse"
          }`}
        >
          <motion.div
            className="w-32 h-32"
            animate={idleAnim}
            transition={{ duration: 0.6, ease: "easeInOut" }}
          >
            <Lottie animationData={lottieData} loop />
          </motion.div>

          <motion.div
            className="relative px-4 py-3 text-sm max-w-xs rounded-2xl shadow-lg bg-gradient-to-r from-primary to-primary-dark text-white"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
          >
            <span
              className={`absolute top-1/2 w-3 h-3 bg-primary rotate-45 ${
                stepInfo.side === "left"
                  ? "-left-1 translate-y-[-50%]"
                  : "-right-1 translate-y-[-50%]"
              }`}
            />
            <motion.span
              animate={{ y: [0, -2, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              {t(stepInfo.text)}
            </motion.span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
